import { Button, CircularProgress, Dialog, DialogActions } from 'drift-ui';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DotCheckError from '../assets/images/dotCheckError.svg';
import DotCheckFound from '../assets/images/dotCheckFound.svg';
import css from './DotCheckModal.scss';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
interface DOTCheckModalProps {
  DOTCheckState?: DOTCheckState;
  DOTCompanyInfo?: DOTCompanyInfo;
  updateDotNumber: (value: string) => void;
  updateCompanyName: (value: string) => void;
  focusDotNumberInput: () => void;
}

type DOTCompanyInfo = {
  dotNumber?: string;
  companyName?: string;
};

export type DOTCompanyResponse = { hasFactoring: boolean; companyName: string; crmNumber: number };

export type DOTCheckState = 'loading' | 'found' | 'not_found' | 'error' | undefined;

const MODALS = {
  loading: LoadingModal,
  found: FoundModal,
  error: ErrorModal,
  // 'not_found' intentionally does not render a modal
};

export const DOTCheckModal: FC<DOTCheckModalProps> = ({
  DOTCheckState,
  DOTCompanyInfo,
  updateDotNumber,
  updateCompanyName,
  focusDotNumberInput,
}) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [currentModal, setCurrentModal] = useState<DOTCheckState>();

  const propsMap = {
    found: {
      updateDotNumber,
      updateCompanyName,
      DOTCompanyInfo,
      setOpenModal,
      focusDotNumberInput,
    },
    error: {
      setOpenModal,
      focusDotNumberInput,
    },
  };

  const Modal = currentModal ? MODALS[currentModal] : null;
  const modalProps = currentModal ? (propsMap[currentModal] ?? {}) : {};
  useEffect(() => {
    setCurrentModal(DOTCheckState);
    if (DOTCheckState === 'not_found' || DOTCheckState === undefined) {
      setOpenModal(false);
    } else {
      setOpenModal(true);
    }
  }, [DOTCheckState]);

  return (
    <Dialog open={openModal} aria-label={t('dot_check.dialog_aria_label')}>
      {Modal && <Modal {...modalProps} />}
    </Dialog>
  );
};

function LoadingModal() {
  const { t } = useTranslation();
  return (
    <div className={css.dialogContainer}>
      <div className={css.container}>
        <div className={css.headerContainer}>
          <p className={css.headerTitle}>{t('dot_check.verifying_your_dot')}</p>
        </div>
        <div className={css.progressContainer}>
          <CircularProgress size={56} />
        </div>
      </div>
    </div>
  );
}

type FoundModalProps = {
  updateDotNumber: (value: string) => void;
  updateCompanyName: (value: string) => void;
  DOTCompanyInfo: DOTCompanyInfo;
  setOpenModal: (value: boolean) => void;
  focusDotNumberInput: () => void;
};

function FoundModal({
  updateDotNumber,
  updateCompanyName,
  DOTCompanyInfo,
  setOpenModal,
  focusDotNumberInput,
}: FoundModalProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const editDOTClicked = async () => {
    setOpenModal(false);
    focusDotNumberInput();
    updateDotNumber('');
    updateCompanyName('');
    dispatch({ type: 'SET_VERIFIED_DOT', verifiedDOT: undefined });
  };

  const continueClicked = async () => {
    localStorage.setItem('DOTCompany', 'true');
    dispatch({ type: 'SET_VERIFIED_DOT', verifiedDOT: DOTCompanyInfo?.dotNumber });
    navigate('/factoring');
  };

  return (
    <div className={cx(css.container, css.foundContainer)}>
      <div className={css.headerContainer}>
        <p className={css.headerTitle}>{`${t('dot_check.dot_found', { number: DOTCompanyInfo?.dotNumber })}`}</p>
        <p className={css.companyText}>{DOTCompanyInfo?.companyName}</p>
        <p>{t('dot_check.company_information_found')}</p>
      </div>
      <div className={css.foundImageContainer}>
        <img
          src={DotCheckFound}
          alt={t('dot_check.alt_found_image')}
          width={200}
          height={164}
          className={css.dotCheckFoundImage}
        />
      </div>
      <div className={css.actionsContainer}>
        <DialogActions className={css.dialogActions}>
          <Button variant="outlined" onClick={editDOTClicked}>
            {t('dot_check.edit_dot')}
          </Button>
          <Button variant="contained" onClick={continueClicked}>
            {t('continue')}
          </Button>
        </DialogActions>
      </div>
    </div>
  );
}

type ErrorModalProps = {
  setOpenModal: (value: boolean) => void;
  focusDotNumberInput: () => void;
};

function ErrorModal({ setOpenModal, focusDotNumberInput }: ErrorModalProps) {
  const { t } = useTranslation();

  const handleContinue = () => {
    setOpenModal(false);
    focusDotNumberInput();
  };

  return (
    <div className={cx(css.container, css.errorContainer)}>
      <div className={css.headerContainer}>
        <p className={css.headerTitle}>{t('dot_check.unable_to_verify_dot')}</p>
        <p>{t('dot_check.verification_failed_message')}</p>
      </div>
      <div className={css.errorImageContainer}>
        <img
          src={DotCheckError}
          alt={t('dot_check.alt_error_image')}
          width={150}
          height={150}
          className={css.dotCheckErrorImage}
        />
      </div>
      <div className={css.actionsContainer}>
        <DialogActions className={css.dialogActions}>
          <Button variant="contained" onClick={handleContinue}>
            {t('Close')}
          </Button>
        </DialogActions>
      </div>
    </div>
  );
}
